/**
 * Error Classifier Utility
 * Converts API errors into user-facing messages with retry logic hints
 */

export const classifyError = (error, endpoint) => {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail || error?.message || '';

  // ===== NETWORK ERRORS (no HTTP status) =====
  if (!status) {
    if (error.code === 'ECONNABORTED' || detail.includes('timeout')) {
      return {
        status: 'timeout',
        message: 'Request timed out. Check your connection.',
        retryable: true,
        endpoint,
        severity: 'warning'
      };
    }
    if (detail.includes('Network Error') || error.code === 'ENOTFOUND' || error.message?.includes('ERR_')) {
      return {
        status: 'network',
        message: 'Unable to reach server. Check your internet connection.',
        retryable: true,
        endpoint,
        severity: 'error'
      };
    }
  }

  // ===== HTTP 400: Bad Request (defensive) =====
  if (status === 400) {
    return {
      status: 400,
      message: `Invalid request to ${endpoint}. Server configuration issue.`,
      retryable: false,
      endpoint,
      severity: 'error'
    };
  }

  // ===== HTTP 401: Unauthorized =====
  if (status === 401) {
    return {
      status: 401,
      message: 'Session expired. Please log in again.',
      retryable: false,
      endpoint,
      severity: 'error',
      requiresRedirect: true
    };
  }

  // ===== HTTP 403: Forbidden =====
  if (status === 403) {
    return {
      status: 403,
      message: 'Access denied to this data. Check your permissions.',
      retryable: false,
      endpoint,
      severity: 'error'
    };
  }

  // ===== HTTP 404: Not Found =====
  if (status === 404) {
    const endpointMessages = {
      'hierarchy-tree': {
        message: 'Hierarchy not available. Showing flat view.',
        severity: 'warning'
      },
      'latest': {
        message: 'No live data available for your account.',
        severity: 'error'
      },
      'status': {
        message: null, // Silent: treat as empty response
        severity: 'silent'
      },
      'get_forecasted_data': {
        message: 'Forecasting data not available for this machine.',
        severity: 'warning'
      },
      'mape': {
        message: null, // Silent: omit accuracy badge
        severity: 'silent'
      }
    };

    const config = endpointMessages[endpoint] || {
      message: `Data not found for ${endpoint}.`,
      severity: 'warning'
    };

    return {
      status: 404,
      message: config.message,
      retryable: false,
      endpoint,
      severity: config.severity
    };
  }

  // ===== HTTP 500: Server Error =====
  if (status === 500) {
    const endpointMessages = {
      'hierarchy-tree': {
        message: 'Hierarchy temporarily unavailable.',
        severity: 'error'
      },
      'latest': {
        message: 'Live data temporarily unavailable.',
        severity: 'error'
      },
      'status': {
        message: null, // Silent: omit health badges
        severity: 'silent'
      },
      'get_forecasted_data': {
        message: 'Forecast service error. Try again later.',
        severity: 'error'
      },
      'mape': {
        message: null, // Silent: omit accuracy badge
        severity: 'silent'
      }
    };

    const config = endpointMessages[endpoint] || {
      message: 'Server error. Try again later.',
      severity: 'error'
    };

    return {
      status: 500,
      message: config.message,
      retryable: true,
      endpoint,
      severity: config.severity
    };
  }

  // ===== Unknown Status Code =====
  return {
    status: status || 'unknown',
    message: detail || 'Unexpected error. Refresh to retry.',
    retryable: true,
    endpoint,
    severity: 'error'
  };
};

/**
 * Determine if UI should display an error banner for this classified error
 */
export const shouldShowError = (classifiedError) => {
  if (!classifiedError) return false;
  return classifiedError.severity !== 'silent';
};

/**
 * Determine if error is fatal (should prevent data display)
 */
export const isFatalError = (classifiedError) => {
  if (!classifiedError) return false;
  // 401 and 403 are fatal; they require action
  return classifiedError.status === 401 || classifiedError.status === 403;
};
